import { useEffect, useMemo, useState } from "react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useAction, usePaginatedQuery, useQuery } from "convex/react"
import { toast } from "sonner"
import { api } from "../../convex/_generated/api"
import { Input } from "@/components/ui/input"
import { formatShortDate } from "@/lib/dates"
import { errorMessage } from "@/lib/errors"
import { Chip } from "@/components/chips"
import { EmptyState } from "@/components/empty-state"
import { EntityRow } from "@/components/entity-row"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { PaywallDialog } from "@/components/paywall-dialog"
import { profileChecklist, profileCompleteness, needsCandidateOnboarding } from "@/lib/candidate"
import { formatSalary } from "@/lib/salary"
import { ReferSomeonePanel, ShareJobButton } from "@/features/jobs"

export const Route = createFileRoute("/candidate/")({
  component: Discover,
})

const PAGE_SIZE = 10

type KindFilter = "all" | "job" | "hackathon"

function Discover() {
  const profile = useQuery(api.profiles.me)
  const apps = useQuery(api.applications.listMine)
  const regs = useQuery(api.hackathons.listMyRegistrations)
  const {
    results: jobs,
    status: jobsPaginationStatus,
    loadMore: loadMoreJobs,
  } = usePaginatedQuery(api.jobs.listPublicPage, {}, { initialNumItems: 30 })
  const hackathons = useQuery(api.hackathons.listPublic)
  const [payOpen, setPayOpen] = useState(false)
  const reconcileCheckout = useAction(
    api.paymentIntentsActions.reconcileMyCheckout,
  )

  // Leftover Payment Link returns and in-app Checkout both land here. The
  // return only *requests* server-side reconciliation against the provider;
  // entitlement still comes exclusively from server-verified evidence.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get("payment") !== "return") return
    window.history.replaceState(null, "", window.location.pathname)
    void (async () => {
      try {
        const result = await reconcileCheckout({ kind: "seeker_annual" })
        if (result.outcome === "settled") {
          toast.success("Payment received — annual access unlocked")
        } else if (result.outcome === "pending_payment") {
          toast.message("Razorpay hasn't confirmed this payment yet — this page updates when it lands.")
        } else if (result.outcome === "support_needed") {
          toast.error(result.message)
        }
      } catch (e) {
        toast.error(errorMessage(e, "Payment check failed"))
      }
    })()
  }, [reconcileCheckout])

  const [search, setSearch] = useState("")
  const [kind, setKind] = useState<KindFilter>("all")
  const [employmentType, setEmploymentType] = useState("all")
  const [page, setPage] = useState(1)

  const loading = jobsPaginationStatus === "LoadingFirstPage" || hackathons === undefined

  const employmentTypes = useMemo(() => {
    if (!jobs) return []
    return Array.from(new Set(jobs.map((job) => job.employmentType))).sort()
  }, [jobs])

  // One feed: open jobs and upcoming hackathons, newest first.
  const feed = useMemo(() => {
    const now = Date.now()
    return [
      ...(jobs ?? []).map((job) => ({
        key: job._id,
        kind: "job" as const,
        createdAt: job._creationTime,
        job,
        hackathon: undefined,
      })),
      ...(hackathons ?? [])
        .filter((h) => h.endsAt >= now)
        .map((h) => ({
          key: h._id,
          kind: "hackathon" as const,
          createdAt: h._creationTime,
          job: undefined,
          hackathon: h,
        })),
    ].sort((a, b) => b.createdAt - a.createdAt)
  }, [jobs, hackathons])

  const filteredFeed = useMemo(() => {
    const q = search.trim().toLowerCase()
    return feed.filter((entry) => {
      if (kind !== "all" && entry.kind !== kind) return false
      if (entry.kind === "job") {
        const job = entry.job!
        if (employmentType !== "all" && job.employmentType !== employmentType) {
          return false
        }
        if (!q) return true
        return (
          job.title.toLowerCase().includes(q) ||
          job.employerName.toLowerCase().includes(q) ||
          job.location.toLowerCase().includes(q) ||
          job.skills.some((skill) => skill.toLowerCase().includes(q))
        )
      }
      if (employmentType !== "all") return false
      const h = entry.hackathon!
      if (!q) return true
      return (
        h.title.toLowerCase().includes(q) ||
        h.employerName.toLowerCase().includes(q) ||
        h.venue.toLowerCase().includes(q)
      )
    })
  }, [feed, search, kind, employmentType])

  const totalPages = Math.max(1, Math.ceil(filteredFeed.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const pageItems = filteredFeed.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  )

  useEffect(() => {
    setPage(1)
  }, [search, kind, employmentType])

  useEffect(() => {
    if (page > totalPages) setPage(totalPages)
  }, [page, totalPages])

  const clearFilters = () => {
    setSearch("")
    setKind("all")
    setEmploymentType("all")
    setPage(1)
  }

  const completeness = profile ? profileCompleteness(profile) : null
  const missing = profile
    ? profileChecklist(profile).filter((item) => !item.done)
    : []

  const hasLive = feed.length > 0
  const rangeStart =
    filteredFeed.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1
  const rangeEnd = Math.min(safePage * PAGE_SIZE, filteredFeed.length)

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="mkt-page-title">Discover</h1>
          <p className="mkt-page-sub">
            Open roles and hackathons from the HSR founders community.
          </p>
        </div>
        {profile && !profile.seekerAccessActive && (
          <button
            type="button"
            className="home-wf-btn home-wf-btn--lime"
            onClick={() => setPayOpen(true)}
          >
            Unlock annual · ₹2,500
          </button>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px] lg:items-start">
        <div className="order-2 lg:order-1">
          {loading && <Skeleton className="h-40 w-full" />}

          {!loading && !hasLive && (
            <EmptyState title="Nothing live right now — check back soon." />
          )}

          {!loading && hasLive && (
            <>
              <div className="mkt-filters !mt-0">
                <Input
                  placeholder="Search title, company, venue…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="max-w-xs"
                />
                <Select
                  value={kind}
                  onValueChange={(value) => setKind(value as KindFilter)}
                >
                  <SelectTrigger className="w-36">
                    <SelectValue placeholder="All kinds" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="job">Jobs</SelectItem>
                    <SelectItem value="hackathon">Hackathons</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={employmentType}
                  onValueChange={setEmploymentType}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    {employmentTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <p className="mt-4 text-sm text-[color:var(--wf-mute)]">
                {filteredFeed.length === 0
                  ? "No matches"
                  : `Showing ${rangeStart}–${rangeEnd} of ${filteredFeed.length}`}
              </p>

              <div className="mt-2">
                {filteredFeed.length === 0 ? (
                  <EmptyState
                    title="No roles or events match your filters."
                    action={{ label: "Clear filters", onClick: clearFilters }}
                  />
                ) : (
                  pageItems.map((entry) =>
                    entry.kind === "job" ? (
                      <EntityRow
                        key={entry.key}
                        to="/jobs/$jobId"
                        params={{ jobId: entry.job!._id }}
                        mark={{
                          name:
                            entry.job!.company?.companyName ??
                            entry.job!.employerName,
                          imageUrl: entry.job!.company?.companyLogoUrl,
                        }}
                        title={entry.job!.title}
                        chips={<Chip emphasis>Job</Chip>}
                        meta={
                          <>
                            {entry.job!.employerName} · {entry.job!.location} ·{" "}
                            {entry.job!.employmentType}
                            {formatSalary(entry.job!)
                              ? ` · ${formatSalary(entry.job!)}`
                              : ""}
                          </>
                        }
                        tags={{ items: entry.job!.skills, max: 5 }}
                        action={
                          <ShareJobButton
                            jobId={entry.job!._id}
                            title={entry.job!.title}
                            companyName={
                              entry.job!.company?.companyName ??
                              entry.job!.employerName
                            }
                            variant="icon"
                          />
                        }
                      />
                    ) : (
                      <EntityRow
                        key={entry.key}
                        to="/hackathons/$hackathonId"
                        params={{ hackathonId: entry.hackathon!._id }}
                        mark={
                          <div
                            className="flex size-11 shrink-0 items-center justify-center rounded-[0.4rem] bg-[rgb(12_15_20/0.06)] text-base font-extrabold text-[color:var(--wf-mute)]"
                            aria-hidden
                          >
                            ◆
                          </div>
                        }
                        title={entry.hackathon!.title}
                        chips={<Chip>Hackathon</Chip>}
                        meta={
                          <>
                            {entry.hackathon!.employerName} ·{" "}
                            {entry.hackathon!.venue} ·{" "}
                            {formatShortDate(entry.hackathon!.startsAt)}
                          </>
                        }
                      />
                    ),
                  )
                )}
              </div>

              {filteredFeed.length > PAGE_SIZE && (
                <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                  <button
                    type="button"
                    className="home-wf-btn home-wf-btn--outline"
                    disabled={safePage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>
                  <p className="text-sm font-medium text-[color:var(--wf-mute)]">
                    Page {safePage} of {totalPages}
                  </p>
                  <button
                    type="button"
                    className="home-wf-btn home-wf-btn--outline"
                    disabled={safePage >= totalPages}
                    onClick={() =>
                      setPage((p) => Math.min(totalPages, p + 1))
                    }
                  >
                    Next
                  </button>
                </div>
              )}
              {jobsPaginationStatus === "CanLoadMore" && kind !== "hackathon" && (
                <button
                  type="button"
                  className="home-wf-btn home-wf-btn--outline mt-4"
                  onClick={() => loadMoreJobs(30)}
                >
                  Load more roles
                </button>
              )}
            </>
          )}
        </div>

        <div className="order-1 space-y-4 lg:order-2">
          {profile && completeness !== null && completeness < 100 && (
            <div className="mkt-panel">
              <p className="mkt-panel__title">
                Profile strength · {completeness}%
              </p>
              <div className="mkt-meter mt-2">
                <div
                  className="mkt-meter__fill"
                  style={{ transform: `scaleX(${completeness / 100})` }}
                />
              </div>
              <p className="mkt-panel__copy">
                Still missing:{" "}
                {missing.map((item) => item.label.toLowerCase()).join(", ")}
              </p>
              <Link
                to={
                  needsCandidateOnboarding(profile)
                    ? "/onboarding/candidate"
                    : "/candidate/settings/profile"
                }
                className="home-wf-btn home-wf-btn--outline mkt-panel__cta"
              >
                Complete profile
              </Link>
            </div>
          )}

          <div className="mkt-panel">
            <p className="mkt-panel__title">Your activity</p>
            {apps === undefined || regs === undefined ? (
              // `?? 0` would read as a confident "0 applications" while the
              // queries are still in flight.
              <Skeleton className="mt-2 h-9 w-full" />
            ) : (
              <p className="mkt-panel__copy">
                {apps.length} application{apps.length === 1 ? "" : "s"} ·{" "}
                {apps.filter((a) => a.status === "reviewed").length} in review
                <br />
                {regs.length} hackathon registration
                {regs.length === 1 ? "" : "s"}
              </p>
            )}
            <Link
              to="/candidate/applications"
              className="home-wf-btn home-wf-btn--outline mkt-panel__cta"
            >
              View all
            </Link>
          </div>

          <ReferSomeonePanel />

          {profile && !profile.seekerAccessActive && (
            <div className="mkt-panel mkt-panel--wash">
              <p className="mkt-panel__copy !mt-0">
                Browsing is free. ₹2,500 a year lets you apply and register
                with one profile.
              </p>
              <button
                type="button"
                className="home-wf-btn home-wf-btn--lime mkt-panel__cta"
                onClick={() => setPayOpen(true)}
              >
                Unlock annual access
              </button>
            </div>
          )}
        </div>
      </div>

      <PaywallDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        kind="seeker_annual"
        onPaid={() => setPayOpen(false)}
      />
    </div>
  )
}
